import { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';

interface BattleCombatant {
  id: string;
  name: string;
  damage: number;
  maxHP: number;
  currentHP: number;
  type: 'hero' | 'enemy' | 'ally';
  teamColor?: string;
  characterId?: string;
}

interface BattleState {
  id: string;
  sessionId: string;
  combatants: BattleCombatant[];
  isActive: boolean;
}

interface BattleDisplayProps {
  battleState: BattleState;
  socket: Socket | null;
}

interface FloatingText {
  targetId: string;
  value: number;
  isHeal: boolean;
  key: number;
  animate: boolean;
}

interface DeathSkull {
  targetId: string;
  key: number;
  animate: boolean;
}

const TEAM_COLORS: Record<string, string> = {
  BLUE: '#3b82f6',
  RED: '#ef4444',
  GREEN: '#22c55e',
  YELLOW: '#eab308',
};

const ALLY_COLOR = '#60a5fa';
const ENEMY_COLOR = '#a78bfa';

function getBarColor(combatant: BattleCombatant): string {
  if (combatant.type === 'enemy') return ENEMY_COLOR;
  if (combatant.type === 'ally') return ALLY_COLOR;
  if (combatant.teamColor && TEAM_COLORS[combatant.teamColor]) {
    return TEAM_COLORS[combatant.teamColor];
  }
  return ALLY_COLOR;
}

const SKULL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="white"><path d="M32 4C18.7 4 8 14.7 8 28c0 8.4 4.3 15.8 10.8 20.1V56c0 2.2 1.8 4 4 4h18.4c2.2 0 4-1.8 4-4v-7.9C51.7 43.8 56 36.4 56 28 56 14.7 45.3 4 32 4zm-8 32a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm16 0a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm-4 10h-8v-4h8v4z"/></svg>`;

type BattleOutcome = 'victory' | 'defeat' | 'draw' | null;

export default function BattleDisplay({ battleState, socket }: BattleDisplayProps) {
  const [combatants, setCombatants] = useState<BattleCombatant[]>(battleState.combatants);
  const [shakingIds, setShakingIds] = useState<Set<string>>(new Set());
  const [glowingIds, setGlowingIds] = useState<Set<string>>(new Set());
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const [deathSkulls, setDeathSkulls] = useState<DeathSkull[]>([]);
  const [outcome, setOutcome] = useState<BattleOutcome>(null);
  const [outcomeVisible, setOutcomeVisible] = useState(false);

  // Track previous HP to detect death
  const [prevHPMap, setPrevHPMap] = useState<Map<string, number>>(() => {
    const m = new Map<string, number>();
    battleState.combatants.forEach((c) => m.set(c.id, c.currentHP));
    return m;
  });

  useEffect(() => {
    if (!socket) return;

    const handleDamage = (data: { battleId: string; targetId: string; damage: number; newHP: number; maxHP: number }) => {
      // Shake
      setShakingIds((prev) => new Set(prev).add(data.targetId));
      setTimeout(() => {
        setShakingIds((prev) => { const next = new Set(prev); next.delete(data.targetId); return next; });
      }, 500);

      // Floating damage text
      const key = Date.now() + Math.random();
      setFloatingTexts((prev) => [...prev, { targetId: data.targetId, value: data.damage, isHeal: false, key, animate: false }]);
      requestAnimationFrame(() => {
        setFloatingTexts((prev) => prev.map((d) => (d.key === key ? { ...d, animate: true } : d)));
      });
      setTimeout(() => {
        setFloatingTexts((prev) => prev.filter((d) => d.key !== key));
      }, 1100);

      // Death skull if HP reached 0 (delayed 2s to not overlap with damage number)
      const prevHP = prevHPMap.get(data.targetId) ?? data.maxHP;
      if (data.newHP <= 0 && prevHP > 0) {
        const skullKey = Date.now() + Math.random();
        setTimeout(() => {
          setDeathSkulls((prev) => [...prev, { targetId: data.targetId, key: skullKey, animate: false }]);
          requestAnimationFrame(() => {
            setDeathSkulls((prev) => prev.map((s) => (s.key === skullKey ? { ...s, animate: true } : s)));
          });
          setTimeout(() => {
            setDeathSkulls((prev) => prev.filter((s) => s.key !== skullKey));
          }, 1500);
        }, 800);
      }
      setPrevHPMap((prev) => new Map(prev).set(data.targetId, data.newHP));
    };

    const handleHeal = (data: { battleId: string; targetId: string; amount: number; newHP: number; maxHP: number }) => {
      // Glow
      setGlowingIds((prev) => new Set(prev).add(data.targetId));
      setTimeout(() => {
        setGlowingIds((prev) => { const next = new Set(prev); next.delete(data.targetId); return next; });
      }, 800);

      // Floating heal text
      const key = Date.now() + Math.random();
      setFloatingTexts((prev) => [...prev, { targetId: data.targetId, value: data.amount, isHeal: true, key, animate: false }]);
      requestAnimationFrame(() => {
        setFloatingTexts((prev) => prev.map((d) => (d.key === key ? { ...d, animate: true } : d)));
      });
      setTimeout(() => {
        setFloatingTexts((prev) => prev.filter((d) => d.key !== key));
      }, 1100);

      setPrevHPMap((prev) => new Map(prev).set(data.targetId, data.newHP));
    };

    const handleStateUpdate = (data: { battleId: string; combatants: BattleCombatant[] }) => {
      setCombatants(data.combatants);
    };

    const handleOutcome = (data: { battleId: string; outcome: 'victory' | 'defeat' | 'draw' }) => {
      setOutcome(data.outcome);
      requestAnimationFrame(() => setOutcomeVisible(true));
    };

    socket.on('battle:damage', handleDamage);
    socket.on('battle:heal', handleHeal);
    socket.on('battle:state-update', handleStateUpdate);
    socket.on('battle:outcome', handleOutcome);

    return () => {
      socket.off('battle:damage', handleDamage);
      socket.off('battle:heal', handleHeal);
      socket.off('battle:state-update', handleStateUpdate);
      socket.off('battle:outcome', handleOutcome);
    };
  }, [socket, prevHPMap]);

  const heroes = combatants.filter((c) => c.type === 'hero' || c.type === 'ally');
  const enemies = combatants.filter((c) => c.type === 'enemy');

  const renderCombatant = useCallback(
    (combatant: BattleCombatant) => {
      const hpPercent = combatant.maxHP > 0 ? (combatant.currentHP / combatant.maxHP) * 100 : 0;
      const isShaking = shakingIds.has(combatant.id);
      const isGlowing = glowingIds.has(combatant.id);
      const isDead = combatant.currentHP <= 0;
      const barColor = getBarColor(combatant);
      const texts = floatingTexts.filter((d) => d.targetId === combatant.id);
      const skulls = deathSkulls.filter((s) => s.targetId === combatant.id);

      return (
        <div
          key={combatant.id}
          className={`relative mb-4 ${isShaking ? 'battle-shake' : ''} ${isGlowing ? 'battle-glow' : ''} ${isDead ? 'opacity-40' : ''}`}
          style={{ transition: 'opacity 0.5s ease' }}
        >
          {/* Floating damage/heal numbers */}
          {texts.map((d) => (
            <span
              key={d.key}
              className={`absolute left-1/2 -translate-x-1/2 font-bold pointer-events-none z-10 ${d.isHeal ? 'text-green-400' : 'text-red-500'}`}
              style={{
                fontSize: '2.5rem',
                top: d.animate ? '-40px' : '10px',
                opacity: d.animate ? 0 : 1,
                transition: 'top 1s ease-out, opacity 1s ease-out',
                textShadow: d.isHeal
                  ? '0 0 10px rgba(0,0,0,0.8), 0 0 20px rgba(74,222,128,0.5)'
                  : '0 0 10px rgba(0,0,0,0.8), 0 0 20px rgba(239,68,68,0.5)',
              }}
            >
              {d.isHeal ? '+' : '-'}{d.value}
            </span>
          ))}

          {/* Death skull */}
          {skulls.map((s) => (
            <div
              key={s.key}
              className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            >
              <div
                className="battle-skull"
                style={{
                  width: s.animate ? '80px' : '20px',
                  height: s.animate ? '80px' : '20px',
                  opacity: s.animate ? 0 : 1,
                  transition: 'width 1s ease-out, height 1s ease-out, opacity 1s ease-out',
                  filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.6))',
                }}
                dangerouslySetInnerHTML={{ __html: SKULL_SVG }}
              />
            </div>
          ))}

          {/* Name and damage */}
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-white font-bold text-2xl truncate mr-4" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
              {combatant.name}
            </span>
            <span className="text-gray-400 text-lg whitespace-nowrap">
              Danno: {combatant.damage}
            </span>
          </div>

          {/* HP bar */}
          <div className="relative h-8 bg-gray-800 rounded-full overflow-hidden border border-gray-600">
            <div
              className="h-full rounded-full"
              style={{
                width: `${hpPercent}%`,
                backgroundColor: barColor,
                transition: 'width 0.5s ease-out',
                boxShadow: `0 0 10px ${barColor}40`,
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
              {combatant.currentHP} / {combatant.maxHP}
            </span>
          </div>
        </div>
      );
    },
    [shakingIds, glowingIds, floatingTexts, deathSkulls]
  );

  return (
    <div className="h-screen w-screen bg-black flex p-8 gap-8 relative overflow-hidden">
      <style>{`
        @keyframes battle-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
          20%, 40%, 60%, 80% { transform: translateX(6px); }
        }
        .battle-shake { animation: battle-shake 0.5s ease-in-out; }
        @keyframes battle-glow-anim {
          0% { box-shadow: 0 0 0px rgba(74,222,128,0); }
          50% { box-shadow: 0 0 24px rgba(74,222,128,0.6), 0 0 48px rgba(74,222,128,0.3); }
          100% { box-shadow: 0 0 0px rgba(74,222,128,0); }
        }
        .battle-glow { animation: battle-glow-anim 0.8s ease-in-out; }
        @keyframes confetti-fall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0.6; }
        }
        @keyframes trumpet-slide-left {
          0% { transform: translateX(-120px) rotate(-15deg); opacity: 0; }
          100% { transform: translateX(0) rotate(-15deg); opacity: 1; }
        }
        @keyframes trumpet-slide-right {
          0% { transform: translateX(120px) rotate(15deg); opacity: 0; }
          100% { transform: translateX(0) rotate(15deg); opacity: 1; }
        }
        @keyframes outcome-text-pop {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes defeat-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes swords-cross {
          0% { transform: scale(0.5) rotate(0deg); opacity: 0; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>

      {/* Left column - Heroes */}
      <div className="flex-1 flex flex-col">
        <h2 className="text-3xl font-bold text-white mb-6 text-center" style={{ textShadow: '0 0 20px rgba(59,130,246,0.5)' }}>
          EROI
        </h2>
        <div className="flex-1 overflow-y-auto pr-2">
          {heroes.map(renderCombatant)}
        </div>
      </div>

      {/* Divider */}
      <div className="w-px bg-gray-700 self-stretch" />

      {/* Right column - Enemies */}
      <div className="flex-1 flex flex-col">
        <h2 className="text-3xl font-bold text-white mb-6 text-center" style={{ textShadow: '0 0 20px rgba(167,139,250,0.5)' }}>
          NEMICI
        </h2>
        <div className="flex-1 overflow-y-auto pl-2">
          {enemies.map(renderCombatant)}
        </div>
      </div>

      {/* Outcome overlay */}
      {outcome && (
        <div
          className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none"
          style={{
            backgroundColor: outcome === 'victory' ? 'rgba(0,0,0,0.5)' : outcome === 'defeat' ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
            opacity: outcomeVisible ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          {outcome === 'victory' && (
            <>
              {/* Confetti */}
              {Array.from({ length: 40 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: '-20px',
                    width: `${8 + Math.random() * 10}px`,
                    height: `${8 + Math.random() * 10}px`,
                    backgroundColor: ['#fbbf24', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#ec4899'][i % 7],
                    borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                    animation: `confetti-fall ${2.5 + Math.random() * 2}s linear ${Math.random() * 1.5}s infinite`,
                  }}
                />
              ))}
              {/* Trumpets */}
              <div className="absolute left-8 top-1/2 -translate-y-1/2 text-8xl" style={{ animation: 'trumpet-slide-left 0.8s ease-out forwards' }}>
                📯
              </div>
              <div className="absolute right-8 top-1/2 -translate-y-1/2 text-8xl" style={{ animation: 'trumpet-slide-right 0.8s ease-out forwards' }}>
                📯
              </div>
              {/* Text */}
              <div className="text-center" style={{ animation: 'outcome-text-pop 0.8s ease-out forwards' }}>
                <div className="text-8xl font-bold text-yellow-400 mb-4" style={{ textShadow: '0 0 40px rgba(251,191,36,0.6), 0 4px 8px rgba(0,0,0,0.8)' }}>
                  VITTORIA!
                </div>
                <div className="text-3xl text-yellow-200" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                  Gli eroi trionfano!
                </div>
              </div>
            </>
          )}

          {outcome === 'defeat' && (
            <>
              {/* Dark fog particles */}
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    top: `${10 + Math.random() * 80}%`,
                    width: `${60 + Math.random() * 100}px`,
                    height: `${60 + Math.random() * 100}px`,
                    background: 'radial-gradient(circle, rgba(127,29,29,0.3) 0%, transparent 70%)',
                    animation: `defeat-pulse ${2 + Math.random() * 2}s ease-in-out ${Math.random()}s infinite`,
                  }}
                />
              ))}
              {/* Skull icon */}
              <div className="text-center" style={{ animation: 'outcome-text-pop 0.8s ease-out forwards' }}>
                <div
                  className="mx-auto mb-6"
                  style={{ width: '120px', height: '120px', filter: 'drop-shadow(0 0 20px rgba(239,68,68,0.5))' }}
                  dangerouslySetInnerHTML={{ __html: SKULL_SVG }}
                />
                <div className="text-8xl font-bold text-red-500" style={{ textShadow: '0 0 40px rgba(239,68,68,0.5), 0 4px 8px rgba(0,0,0,0.8)' }}>
                  SCONFITTA
                </div>
                <div className="text-3xl text-red-300 mt-4" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                  Gli eroi sono stati sconfitti...
                </div>
              </div>
            </>
          )}

          {outcome === 'draw' && (
            <div className="text-center" style={{ animation: 'outcome-text-pop 0.8s ease-out forwards' }}>
              <div className="text-8xl mb-6" style={{ animation: 'swords-cross 0.8s ease-out forwards' }}>
                ⚔️
              </div>
              <div className="text-8xl font-bold text-gray-300" style={{ textShadow: '0 0 40px rgba(156,163,175,0.4), 0 4px 8px rgba(0,0,0,0.8)' }}>
                PAREGGIO
              </div>
              <div className="text-3xl text-gray-400 mt-4" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                La battaglia non ha un vincitore
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
